import { useMemo, useState } from 'react'
import Icon from '../Icon'
import { go, useContent, useLive, type Graded, type Inbound, type LiveState, type RunInfo } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { buzz, celebrate, floatText, resultKick, sound } from '../../lib/fun'
import { CALL_LINES, callSituation, isAnswerable, levelFor, pick, type AnswerValue } from '../../../shared/classroom'
import { AnswerPad, Feedback, Rich, StepBody } from './Step'
import Pichi, { EndBird, stageFor } from './Pichi'
import type { Look } from '../../../shared/shop'

/**
 * The student's phone during class. It shows what the teacher has on screen,
 * the answer buttons while the question is open, instant feedback, and — if
 * the teacher calls on them — a full-screen "¡Te toca!".
 */
export default function LiveStudent({ runId, userId, xpBefore, look, onEnded }: { runId: string; userId: string; xpBefore: number; look?: Look; onEnded?: () => void }) {
  const modules = useContent()
  const [state, setState] = useState<LiveState | null>(null)
  const [graded, setGraded] = useState<Record<string, Graded>>({})
  // Sent but not graded: the question is still open and the student may change it.
  const [chosen, setChosen] = useState<Record<string, AnswerValue>>({})
  const [pending, setPending] = useState<string | null>(null)
  const [earned, setEarned] = useState(0)
  const [streak, setStreak] = useState(0)
  const [review, setReview] = useState<string[] | null>(null)
  const [ended, setEnded] = useState<RunInfo['board'] | null>(null)
  const [lastCall, setLastCall] = useState<string | null>(null)

  const lesson = useMemo(() => {
    for (const m of modules ?? []) for (const l of m.lessons) if (l.id === state?.lessonId) return l
    return null
  }, [modules, state?.lessonId])

  const onMessage = (m: Inbound) => {
    switch (m.t) {
      case 'state': {
        setState(m)
        const key = m.callOn ? `${m.callOn.userId}:${m.callOn.stepId}:${m.step}` : null
        if (m.callOn?.userId === userId && key !== lastCall) {
          setLastCall(key)
          sound('callon')
          buzz([80, 60, 80, 60, 160])
        }
        break
      }
      case 'graded': {
        setPending(null)
        setGraded((g) => ({ ...g, [m.stepId]: m }))
        if (!m.repeat) {
          const before = levelFor(xpBefore + earned).index
          setEarned((e) => e + m.xp)
          setStreak(m.streak)
          resultKick(m.result, m.xp)
          if (levelFor(xpBefore + earned + m.xp).index > before) setTimeout(() => { sound('level'); celebrate() }, 700)
        }
        break
      }
      case 'received':
        setPending(null)
        setChosen((c) => ({ ...c, [m.stepId]: m.answer }))
        break
      case 'refused':
        setPending(null)
        break
      case 'bonus':
        setEarned((e) => e + m.xp)
        resultKick('right', m.xp)
        floatText(w('callBonus'), { x: innerWidth / 2, y: innerHeight / 2 }, 'tap')
        break
      case 'reteach':
        setReview(m.stepIds)
        sound('poll')
        break
      case 'ended':
        onEnded?.()
        setEnded(m.summary?.board ?? [])
        celebrate()
        sound('level')
        break
    }
  }
  const { status, send } = useLive(runId, onMessage)

  const answer = (stepId: string, a: AnswerValue) => {
    setPending(stepId)
    send({ t: 'answer', stepId, answer: a })
  }

  if (ended || status === 'ended' || state?.ended) {
    if (!ended) onEnded?.()
    const board = ended ?? []
    const mine = board.findIndex((r) => r.user_id === userId)
    return (
      <div className="live-student done-screen">
        <EndBird level={levelFor(xpBefore + earned).index} look={look} />
        <h1>{w('lessonOver')}</h1>
        <p className="big-xp">{w('youEarned')} <strong>+{earned} {w('oro')}</strong></p>
        {mine >= 0 && <p className="muted">#{mine + 1} / {board.length}</p>}
        {board.length > 0 && <Board board={board} me={userId} />}
        {lesson && <button className="primary big" onClick={() => go(`/study/${lesson.id}`)}><Icon name="book" /> {w('keepStudying')}</button>}
        <button className="big home-btn" onClick={() => go('/')}><Icon name="home" /> {w('home')}</button>
      </div>
    )
  }
  if (status === 'gone') return <div className="live-student"><p className="card">{w('ended')}</p><button onClick={() => go('/')}>{w('home')}</button></div>
  if (!state || !lesson) return <div className="boot">{status === 'reconnecting' ? w('reconnecting') : w('connecting')}</div>

  const step = lesson.steps[state.step]
  if (!step) return null
  const mine = graded[step.id]
  const calledMe = state.callOn?.userId === userId
  const callLine = calledMe && state.callOn ? L(pick(CALL_LINES[callSituation(state.callOn.kind, state.callOn.result)], seeded(`${state.callOn.stepId}:${state.step}`))) : ''
  const stage = stageFor(levelFor(xpBefore + earned).index)

  return (
    <div className="live-student">
      <header className="live-top">
        <span className="small">{L(lesson.title)}</span>
        <span className="live-chips">
          {streak >= 2 && <span className="chip hot"><Icon name="flame" size={16} /> {streak}</span>}
          <span className="chip chip-oro"><Icon name="coin" size={16} /> {earned}</span>
          <span className="chip muted">{state.step + 1}/{state.total}</span>
        </span>
      </header>
      {status === 'reconnecting' && <p className="warn small">{w('reconnecting')}</p>}

      <div className="progress"><div style={{ width: `${((state.step + 1) / state.total) * 100}%` }} /></div>

      {isAnswerable(step) && <StepBody step={step} />}

      {isAnswerable(step) && (
        mine ? (
          <Feedback
            step={step} result={mine.result} xp={mine.repeat ? 0 : mine.xp} streak={mine.streak}
          />
        ) : state.open ? (
          <>
            <AnswerPad key={step.id} step={step} chosen={chosen[step.id]} disabled={pending === step.id} onAnswer={(a) => answer(step.id, a)} />
            {chosen[step.id] && <p className="waiting can-change">{w('canChange')}</p>}
          </>
        ) : (
          <p className="waiting">{w('closed')}</p>
        )
      )}
      {!isAnswerable(step) && (
        <div className="eyes-up">
          <Pichi size={170} stage={stage} mood="look" look={look} />
          <h1>{w('eyesUp')}</h1>
          <p>{w('eyesUpSub')}</p>
          {step.type !== 'discuss' && 'title' in step && <span className="topic">{L(step.title)}</span>}
          {step.type === 'discuss' && <span className="topic">{w('discuss')}</span>}
        </div>
      )}

      {calledMe && (
        <div className="callon-overlay" role="alertdialog" aria-live="assertive">
          <div className="callon-card">
            {/* Explaining a right answer = teacher time: Pichi wears the graduation cap. */}
            <Pichi size={130} stage={stage} mood="cheer" look={state.callOn && callSituation(state.callOn.kind, state.callOn.result) === 'right' ? { ...look, hat: 'birrete' } : look} />
            <h1>{w('yourTurn')}</h1>
            <p>{callLine}</p>
          </div>
        </div>
      )}

      {review && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal-card">
            <h2><Icon name="refresh" /> {w('reviewForYou')}</h2>
            <p className="muted small">{w('reviewNote')}</p>
            {review.map((id) => {
              const s = lesson.steps.find((x) => x.id === id)
              if (!s) return null
              return (
                <div key={id} className="review-item">
                  <StepBody step={s} />
                  {s.type === 'question' && <div className="fb-explain"><Rich text={L(s.explain)} /></div>}
                </div>
              )
            })}
            <button className="primary" onClick={() => { setReview(null); celebrate() }}>{w('gotIt')}</button>
          </div>
        </div>
      )}
    </div>
  )
}

export function Board({ board, me }: { board: RunInfo['board']; me?: string }) {
  return (
    <ol className="board">
      {board.slice(0, 5).map((r, i) => (
        <li key={r.user_id} className={r.user_id === me ? 'me' : ''}>
          <span className="board-rank">{i + 1}</span>
          <span className="board-name">{r.name}</span>
          <span className="board-xp"><Icon name="coin" size={16} /> {r.xp}</span>
        </li>
      ))}
    </ol>
  )
}

/** A stable "random" for one call-on, so the line doesn't change on every re-render. */
function seeded(key: string): () => number {
  let h = 2166136261
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619)
  return () => ((h >>> 0) % 1000) / 1000
}
